import React, { useState } from 'react';
import { TradeList } from './TradeList';
import { ActionButtons } from './ActionButtons';
import { UtilityRow } from './UtilityRow';
import { ActionButtonType } from './ActionButtons';
import { Tradesperson } from '@/lib/types';
import { TradesPurpleHeader } from './TradesPurpleHeader';
import { useAuth } from '@/contexts/AuthContext';

interface TradeListContainerProps {
  postcodeOrUsername?: string;
  categoryFilter?: string | null;
  onCategoryChange?: (category: string | null) => void;
  highlightedTradesperson?: Tradesperson | null;
  mode?: 'personal' | 'public' | 'results' | 'search';
  dataSource?: 'user' | 'username' | 'postcode' | 'popular' | 'provided';
  providedTradespeople?: Tradesperson[];
  syncWithUrl?: boolean;
  onTradespersonSaved?: (trade: Tradesperson) => void;
  onDataLoaded?: (trades: Tradesperson[]) => void;
  showHeader?: boolean;
  showActionButtons?: boolean;
  refreshTrigger?: number;
}

export function TradeListContainer({
  postcodeOrUsername,
  categoryFilter = null,
  onCategoryChange,
  highlightedTradesperson = null,
  mode = 'personal',
  dataSource = 'user',
  providedTradespeople,
  syncWithUrl = false,
  onTradespersonSaved,
  onDataLoaded,
  showHeader = true,
  showActionButtons = true,
  refreshTrigger
}: TradeListContainerProps) {
  const { isAuthenticated } = useAuth();
  const [activeButton, setActiveButton] = useState<ActionButtonType | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Check if we have tradespeople to enable/disable the quote button
  const hasTradespeople = providedTradespeople?.length ? providedTradespeople.length > 0 : true;

  const handleActionButtonClick = (button: ActionButtonType) => {
    setActiveButton(button === activeButton ? null : button);
    // Dispatch event for other components to react to
    window.dispatchEvent(new CustomEvent('tradespurple-action', { detail: { action: button } }));
  };

  const handleFilterButtonClick = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="flex flex-col">
      {/* Condensed header for logged-in users - only show if showHeader is true */}
      {isAuthenticated && showHeader && <TradesPurpleHeader forceCompact={true} />}

      {/* Large Primary Action Buttons */}
      {showActionButtons && (
        <div className="px-4 pt-4 pb-2">
          <ActionButtons
            activeButton={activeButton}
            onButtonClick={handleActionButtonClick}
            hasTradespeople={hasTradespeople}
            size="large"
            layout="stacked"
          />
        </div>
      )}

      {/* Utility Row with Filter button and utility actions */}
      <div className="px-4 pb-2">
        <UtilityRow
          onFilterClick={handleFilterButtonClick}
          onButtonClick={handleActionButtonClick}
          activeFilter={showFilters}
          activeButton={activeButton}
        />
      </div>

      {/* Trade List with optional category filter visibility */}
      <TradeList
        postcodeOrUsername={postcodeOrUsername}
        categoryFilter={categoryFilter}
        onCategoryChange={onCategoryChange}
        highlightedTradesperson={highlightedTradesperson}
        mode={mode}
        dataSource={dataSource}
        providedTradespeople={providedTradespeople}
        syncWithUrl={syncWithUrl}
        onTradespersonSaved={onTradespersonSaved}
        onDataLoaded={onDataLoaded}
        showCategoryFilter={showFilters}
        showTitle={false}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
}
